/**
 * Jubly Equity Page — Public page explaining the contributor-ownership model
 * Based on the Shalom Co-op proposal (Isaiah 61, Luke 4)
 */

import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import JublyLogo from '../components/JublyLogo';
import { useState, useEffect } from 'react';

function Stat({ value, label }) {
  return (
    <div className="text-center">
      <div className="text-3xl font-bold text-blue-700">{value}</div>
      <div className="text-sm text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function Pillar({ number, title, description }) {
  return (
    <div className="flex gap-4">
      <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold flex-shrink-0">{number}</div>
      <div>
        <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
        <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function PointCategory({ type, examples, weight }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div>
        <p className="text-sm font-medium text-gray-900">{type}</p>
        <p className="text-xs text-gray-500">{examples}</p>
      </div>
      <span className="text-xs font-mono bg-blue-50 text-blue-700 px-2 py-1 rounded-full">{weight}</span>
    </div>
  );
}

export default function EquityPage() {
  const { isAuthenticated, user } = useAuth();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetch('/api/contributor/profile', { credentials: 'include' })
        .then(r => r.json())
        .then(d => setProfile(d.data))
        .catch(() => {});
    }
  }, [isAuthenticated]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <div className="mb-6">
            <JublyLogo size={64} className="mx-auto" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">
            Own What You Build
          </h1>
          <p className="text-xl text-blue-200 mb-2 max-w-2xl mx-auto">
            Every contribution to Jubly earns you real ownership. Bug reports, feature ideas, code, support — everything counts.
          </p>
          <p className="text-sm text-blue-300 mb-8">
            Based on the principle of Jubilee (Isaiah 61, Luke 4) — those who have nothing are lifted up.
          </p>

          {isAuthenticated && profile?.isContributor ? (
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur rounded-2xl px-6 py-3">
              <span className="text-3xl font-bold text-amber-300">{(profile.points || 0).toLocaleString()}</span>
              <div className="text-left">
                <span className="text-sm font-medium block">Your contribution points</span>
                <span className="text-xs text-blue-300">Level: {profile.level}</span>
              </div>
            </div>
          ) : (
            <div className="flex gap-3 justify-center">
              {isAuthenticated ? (
                <button
                  onClick={() => fetch('/api/contributor/register', { method: 'POST', credentials: 'include' }).then(() => window.location.reload())}
                  className="px-6 py-3 bg-white text-blue-900 font-semibold rounded-xl hover:bg-blue-50 transition-colors shadow-lg"
                >
                  Become a Pre-Contributor
                </button>
              ) : (
                <Link to="/signup" className="px-6 py-3 bg-white text-blue-900 font-semibold rounded-xl hover:bg-blue-50 transition-colors shadow-lg">
                  Join Jubly
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Beta Disclaimer */}
      <div className="max-w-4xl mx-auto px-6 -mt-8 mb-6 relative z-10">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-900 font-medium">LLC Formation In Progress</p>
          <p className="text-xs text-amber-700 mt-1 leading-relaxed">
            Jubly, LLC is currently in the process of being formed. The equity and ownership model described on this page is in active development as we finalize the legal structure. All contributors are currently considered <strong>pre-contributors</strong> — your points are being tracked and will be honored when the LLC formation is complete. We are committed to recognizing early contributions in the final ownership structure.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-4xl mx-auto px-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 grid grid-cols-3 gap-8">
          <Stat value="⅓" label="Of profit to jubly.org (empower the vulnerable)" />
          <Stat value="⅓" label="Quarterly dividends to contributors" />
          <Stat value="100%" label="Transparent pricing (cost + 45%)" />
        </div>
      </div>

      {/* Five Pillars */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">The Five Pillars</h2>
        <p className="text-gray-600 mb-8">Our ownership model is built on five interconnected principles.</p>

        <div className="space-y-6">
          <Pillar number="1" title="Contribution-Accreted Ownership"
            description="Contribution points are earned continuously through every form of value-add. No share is permanently fixed — ownership flows to those actively building." />
          <Pillar number="2" title="Transparent, Formulaic Pricing"
            description="A fixed 45% margin above cost (includes taxes). No price gouging, no extraction. Customers know exactly what they're paying for." />
          <Pillar number="3" title="Covenanted Profit Distribution"
            description="Every dollar of profit is split three ways by covenant: ⅓ to jubly.org (empowering the vulnerable), ⅓ to contributor dividends, ⅓ to future investment and infrastructure." />
          <Pillar number="4" title="Multi-Stakeholder Governance"
            description="Decision-making is distributed and tied to contribution — not to initial capital invested. Committees govern product, engineering, and policy decisions democratically." />
          <Pillar number="5" title="Enforceable Community Standards"
            description="Ownership carries moral responsibility. Abusive or harmful conduct results in point withdrawal. We build a community where dignity is non-negotiable." />
        </div>
      </div>

      {/* How Points Work */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">How Contribution Points Work</h2>
          <p className="text-gray-600 mb-8">Every form of meaningful contribution earns ownership. Points use a rolling 3-year window — rewarding sustained engagement, not just early participation.</p>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <PointCategory type="Bug Reports & QA" examples="Finding issues, testing, quality assurance" weight="2x base" />
            <PointCategory type="Feature Ideas" examples="Suggestions that ship, strategic input" weight="1-3x base" />
            <PointCategory type="Code & Development" examples="Code contributions, architecture, technical design" weight="3x base" />
            <PointCategory type="Customer Support" examples="Helping users, documentation, onboarding" weight="1.5x base" />
            <PointCategory type="Sales & Partnerships" examples="Customer referrals, partner introductions" weight="2x + revenue %" />
            <PointCategory type="Community & Care" examples="Moderation, culture-building, mentoring" weight="1.5x base" />
            <PointCategory type="Committee Participation" examples="Discussion, voting, governance work" weight="1-3 pts/action" />
          </div>

          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h4 className="font-semibold text-blue-900 mb-2">Points Are Not Securities</h4>
            <p className="text-sm text-blue-800">Contribution points are earned through your own labor, are non-transferable, and cannot be sold. Economic benefit comes only through the company's dividend distributions. This design keeps points outside securities regulation while still providing real ownership value.</p>
          </div>
        </div>
      </div>

      {/* Revenue Model — Sankey Diagram */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Where Every Dollar Goes</h2>
          <p className="text-gray-600 mb-8">Radical transparency. Our pricing is cost + 45% margin. After taxes (~22%), the profit is split equally three ways by covenant.</p>

          {/* Sankey Diagram — 5 columns: Customer → Split → Tax → 3 Funds → Community Distribution */}
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 sm:p-6 overflow-x-auto">
            <svg viewBox="0 0 1200 520" className="w-full" style={{ minWidth: 780 }}>
              <defs>
                <linearGradient id="sk-cost" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#94a3b8"/><stop offset="100%" stopColor="#94a3b8"/></linearGradient>
                <linearGradient id="sk-markup" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#3b82f6"/><stop offset="100%" stopColor="#6366f1"/></linearGradient>
                <linearGradient id="sk-tax" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#ef4444"/><stop offset="100%" stopColor="#dc2626"/></linearGradient>
                <linearGradient id="sk-after" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#6366f1"/><stop offset="100%" stopColor="#4f46e5"/></linearGradient>
                <linearGradient id="sk-org" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#4f46e5"/><stop offset="100%" stopColor="#10b981"/></linearGradient>
                <linearGradient id="sk-div" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#4f46e5"/><stop offset="100%" stopColor="#f59e0b"/></linearGradient>
                <linearGradient id="sk-inv" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#4f46e5"/><stop offset="100%" stopColor="#8b5cf6"/></linearGradient>
                <linearGradient id="sk-proj" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#10b981"/><stop offset="100%" stopColor="#059669"/></linearGradient>
                <linearGradient id="sk-cont" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#f59e0b"/><stop offset="100%" stopColor="#d97706"/></linearGradient>
                <linearGradient id="sk-capex" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#8b5cf6"/><stop offset="100%" stopColor="#6d28d9"/></linearGradient>
              </defs>

              {/* ═══ Col 1: Customer Payment ═══ */}
              <rect x="10" y="70" width="100" height="260" rx="8" fill="#e2e8f0" stroke="#cbd5e1"/>
              <text x="60" y="182" textAnchor="middle" fill="#334155" fontWeight="600" fontSize="14">Customer</text>
              <text x="60" y="200" textAnchor="middle" fill="#64748b" fontSize="11">Payment</text>
              <text x="60" y="226" textAnchor="middle" fill="#334155" fontWeight="700" fontSize="20">$145</text>

              {/* ═══ Flow: Cost (69%) → Service Delivery ═══ */}
              <path d="M110 130 C170 130, 185 100, 240 100" stroke="url(#sk-cost)" strokeWidth="40" fill="none" opacity="0.18"/>
              <rect x="240" y="76" width="120" height="48" rx="6" fill="#e2e8f0" stroke="#94a3b8"/>
              <text x="300" y="96" textAnchor="middle" fill="#475569" fontWeight="600" fontSize="12">Service Delivery</text>
              <text x="300" y="114" textAnchor="middle" fill="#64748b" fontSize="10">$100 (cost)</text>
              <text x="173" y="108" fill="#64748b" fontSize="10" fontWeight="500">69%</text>

              {/* ═══ Flow: 31% Margin → Gross Profit ═══ */}
              <path d="M110 250 C170 250, 185 250, 240 250" stroke="url(#sk-markup)" strokeWidth="24" fill="none" opacity="0.2"/>
              <rect x="240" y="226" width="120" height="48" rx="6" fill="#eff6ff" stroke="#3b82f6"/>
              <text x="300" y="246" textAnchor="middle" fill="#1e40af" fontWeight="600" fontSize="12">31% Margin</text>
              <text x="300" y="264" textAnchor="middle" fill="#3b82f6" fontSize="10">$45 gross profit</text>
              <text x="173" y="242" fill="#3b82f6" fontSize="10" fontWeight="500">31%</text>

              {/* ═══ Col 2.5: Tax Split ═══ */}

              {/* → Tax (22% of profit) */}
              <path d="M360 238 C400 238, 410 170, 440 170" stroke="url(#sk-tax)" strokeWidth="8" fill="none" opacity="0.25"/>
              <rect x="440" y="148" width="110" height="44" rx="6" fill="#fef2f2" stroke="#fca5a5"/>
              <text x="495" y="166" textAnchor="middle" fill="#dc2626" fontWeight="600" fontSize="12">🏛 Tax</text>
              <text x="495" y="183" textAnchor="middle" fill="#ef4444" fontSize="10">~$10 (22%)*</text>

              {/* → After-tax profit */}
              <path d="M360 255 C400 255, 410 290, 440 290" stroke="url(#sk-after)" strokeWidth="18" fill="none" opacity="0.2"/>
              <rect x="440" y="268" width="110" height="44" rx="6" fill="#eef2ff" stroke="#6366f1"/>
              <text x="495" y="286" textAnchor="middle" fill="#4338ca" fontWeight="600" fontSize="12">After Tax</text>
              <text x="495" y="303" textAnchor="middle" fill="#4f46e5" fontSize="10">$35 net profit</text>

              {/* Tax deduction note */}
              <text x="495" y="210" textAnchor="middle" fill="#94a3b8" fontSize="8" fontStyle="italic">*jubly.org donations</text>
              <text x="495" y="220" textAnchor="middle" fill="#94a3b8" fontSize="8" fontStyle="italic">are tax-deductible</text>

              {/* ═══ Col 3: Three Funds (from after-tax profit) ═══ */}

              {/* → jubly.org */}
              <path d="M550 278 C590 278, 600 100, 640 100" stroke="url(#sk-org)" strokeWidth="12" fill="none" opacity="0.22"/>
              <rect x="640" y="72" width="150" height="56" rx="8" fill="#ecfdf5" stroke="#10b981"/>
              <text x="658" y="92" fill="#059669" fontWeight="700" fontSize="13">🌱 jubly.org</text>
              <text x="658" y="110" fill="#047857" fontSize="10">⅓ ($11.67)</text>
              <text x="658" y="123" fill="#047857" fontSize="8" opacity="0.7">Empower the vulnerable</text>

              {/* → Dividends */}
              <path d="M550 290 C590 290, 600 255, 640 255" stroke="url(#sk-div)" strokeWidth="12" fill="none" opacity="0.22"/>
              <rect x="640" y="227" width="150" height="56" rx="8" fill="#fffbeb" stroke="#f59e0b"/>
              <text x="658" y="247" fill="#b45309" fontWeight="700" fontSize="13">💰 Dividends</text>
              <text x="658" y="265" fill="#92400e" fontSize="10">⅓ ($11.67)</text>
              <text x="658" y="278" fill="#92400e" fontSize="8" opacity="0.7">Contributor payouts</text>

              {/* → Jubly CapEx */}
              <path d="M550 302 C590 302, 600 400, 640 400" stroke="url(#sk-inv)" strokeWidth="12" fill="none" opacity="0.22"/>
              <rect x="640" y="372" width="150" height="56" rx="8" fill="#f5f3ff" stroke="#8b5cf6"/>
              <text x="658" y="392" fill="#6d28d9" fontWeight="700" fontSize="13">🚀 Jubly CapEx</text>
              <text x="658" y="410" fill="#5b21b6" fontSize="10">⅓ ($11.67)</text>
              <text x="658" y="423" fill="#5b21b6" fontSize="8" opacity="0.7">Growth & infrastructure</text>

              {/* ⅓ labels */}
              <text x="605" y="92" fill="#059669" fontSize="8" fontWeight="500">⅓</text>
              <text x="605" y="247" fill="#b45309" fontSize="8" fontWeight="500">⅓</text>
              <text x="605" y="392" fill="#6d28d9" fontSize="8" fontWeight="500">⅓</text>

              {/* ═══ Col 4: Community Distribution ═══ */}

              {/* jubly.org → Community-voted projects */}
              <path d="M790 92 C825 92, 840 55, 880 55" stroke="url(#sk-proj)" strokeWidth="9" fill="none" opacity="0.2"/>
              <path d="M790 105 C825 105, 840 105, 880 105" stroke="url(#sk-proj)" strokeWidth="5" fill="none" opacity="0.15"/>
              <path d="M790 115 C825 115, 840 140, 880 140" stroke="url(#sk-proj)" strokeWidth="3" fill="none" opacity="0.12"/>

              <rect x="880" y="32" width="210" height="44" rx="6" fill="#d1fae5" stroke="#6ee7b7"/>
              <text x="896" y="50" fill="#065f46" fontWeight="600" fontSize="11">Education Program</text>
              <text x="896" y="66" fill="#047857" fontSize="9">$5.20 — 412 community votes</text>

              <rect x="880" y="84" width="210" height="44" rx="6" fill="#ecfdf5" stroke="#a7f3d0"/>
              <text x="896" y="102" fill="#065f46" fontWeight="600" fontSize="11">Clean Water Initiative</text>
              <text x="896" y="118" fill="#047857" fontSize="9">$3.80 — 298 community votes</text>

              <rect x="880" y="136" width="210" height="34" rx="6" fill="#f0fdf4" stroke="#bbf7d0"/>
              <text x="896" y="157" fill="#065f46" fontWeight="600" fontSize="10">Micro-loans Fund — $2.67</text>

              {/* Dividends → Contributors */}
              <path d="M790 247 C825 247, 840 225, 880 225" stroke="url(#sk-cont)" strokeWidth="8" fill="none" opacity="0.2"/>
              <path d="M790 260 C825 260, 840 268, 880 268" stroke="url(#sk-cont)" strokeWidth="5" fill="none" opacity="0.15"/>
              <path d="M790 270 C825 270, 840 305, 880 305" stroke="url(#sk-cont)" strokeWidth="3" fill="none" opacity="0.12"/>

              <rect x="880" y="206" width="210" height="40" rx="6" fill="#fef3c7" stroke="#fcd34d"/>
              <text x="896" y="224" fill="#78350f" fontWeight="600" fontSize="11">Sarah M. — $4.10</text>
              <text x="896" y="238" fill="#92400e" fontSize="9">2,840 points (24.3%)</text>

              <rect x="880" y="254" width="210" height="36" rx="6" fill="#fffbeb" stroke="#fde68a"/>
              <text x="896" y="274" fill="#78350f" fontWeight="600" fontSize="11">James K. — $2.50</text>

              <rect x="880" y="298" width="210" height="32" rx="6" fill="#fefce8" stroke="#fef08a"/>
              <text x="896" y="318" fill="#78350f" fontWeight="600" fontSize="10">Amara T. — $0.40</text>

              {/* CapEx → Investment projects */}
              <path d="M790 392 C825 392, 840 375, 880 375" stroke="url(#sk-capex)" strokeWidth="7" fill="none" opacity="0.2"/>
              <path d="M790 405 C825 405, 840 415, 880 415" stroke="url(#sk-capex)" strokeWidth="5" fill="none" opacity="0.15"/>
              <path d="M790 415 C825 415, 840 450, 880 450" stroke="url(#sk-capex)" strokeWidth="3" fill="none" opacity="0.12"/>

              <rect x="880" y="356" width="210" height="40" rx="6" fill="#ede9fe" stroke="#c4b5fd"/>
              <text x="896" y="374" fill="#4c1d95" fontWeight="600" fontSize="11">AI Infrastructure — $5.00</text>
              <text x="896" y="388" fill="#5b21b6" fontSize="9">389 community votes</text>

              <rect x="880" y="404" width="210" height="34" rx="6" fill="#f5f3ff" stroke="#ddd6fe"/>
              <text x="896" y="424" fill="#4c1d95" fontWeight="600" fontSize="10">Mobile App Dev — $3.80</text>

              <rect x="880" y="446" width="210" height="34" rx="6" fill="#faf5ff" stroke="#e9d5ff"/>
              <text x="896" y="466" fill="#4c1d95" fontWeight="600" fontSize="10">API Platform — $2.87</text>

              {/* Column headers */}
              <text x="60" y="22" textAnchor="middle" fill="#94a3b8" fontSize="10" fontWeight="600" letterSpacing="0.12em">REVENUE</text>
              <text x="300" y="22" textAnchor="middle" fill="#94a3b8" fontSize="10" fontWeight="600" letterSpacing="0.12em">ALLOCATION</text>
              <text x="495" y="22" textAnchor="middle" fill="#94a3b8" fontSize="10" fontWeight="600" letterSpacing="0.12em">TAX</text>
              <text x="715" y="22" textAnchor="middle" fill="#94a3b8" fontSize="10" fontWeight="600" letterSpacing="0.12em">THREE FUNDS</text>
              <text x="985" y="22" textAnchor="middle" fill="#94a3b8" fontSize="10" fontWeight="600" letterSpacing="0.12em">COMMUNITY DECIDES</text>

              {/* Footnote */}
              <text x="10" y="505" fill="#94a3b8" fontSize="8">* Effective ~22% tax rate via Subchapter T cooperative election + QBI deduction. jubly.org donations (⅓ of profit) are tax-deductible, further reducing tax burden.</text>
            </svg>
          </div>
        </div>
      </div>

      {/* Governance */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Democratic Governance</h2>
          <p className="text-gray-600 mb-8">Five committees govern different aspects of the platform, as established in the Jubly Equity Constitution. Any contributor can apply to join.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'Strategy Committee', desc: 'Mission, direction, partnerships, capital strategy, and long-term vision', icon: '🧭' },
              { name: 'Ethics Committee', desc: 'Harm reports, safety concerns, community standards enforcement', icon: '🛡️' },
              { name: 'Product Committee', desc: 'Features, UX, roadmap, bug triage, and contributor coordination', icon: '📦' },
              { name: 'Legal Committee', desc: 'IP, compliance, privacy, contracts, and regulatory matters', icon: '⚖️' },
              { name: "Founders' Circle", desc: 'Constitutional amendments, ratification, and foundational governance', icon: '🔑' },
            ].map(c => (
              <div key={c.name} className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="text-2xl mb-2">{c.icon}</div>
                <h4 className="font-semibold text-gray-900 mb-1">{c.name}</h4>
                <p className="text-sm text-gray-500">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Biblical Foundation */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">The Jubilee Principle</h2>
          <p className="text-gray-600 mb-6">Jubly's name comes from the biblical concept of Jubilee — a radical reset where debts are forgiven, land is returned, and those who have nothing are restored to wholeness.</p>

          <blockquote className="border-l-4 border-amber-400 pl-6 py-2 my-6">
            <p className="text-lg text-gray-700 italic">"The Spirit of the Lord is on me, because he has anointed me to proclaim good news to the poor. He has sent me to proclaim freedom for the prisoners and recovery of sight for the blind, to set the oppressed free, to proclaim the year of the Lord's favor."</p>
            <cite className="text-sm text-gray-500 mt-2 block">— Luke 4:18-19 (quoting Isaiah 61)</cite>
          </blockquote>

          <p className="text-gray-600">In practice, this means: technology should lift people up, not extract from them. When you contribute to Jubly — whether by reporting a bug, suggesting a feature, or helping another user — you're not just improving software. You're building equity in a company designed to share its success with everyone who makes it possible.</p>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-br from-blue-900 to-purple-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to own what you build?</h2>
          <p className="text-blue-200 mb-4 max-w-xl mx-auto">Join contributors who are shaping a more equitable technology company. Every contribution counts.</p>
          <p className="text-blue-300/70 text-xs mb-8 max-w-lg mx-auto">The Jubly business entity is still in formation. Early sign-ups are pre-contributors — your points are tracked and will be honored when the legal structure is finalized.</p>
          {isAuthenticated ? (
            <Link to="/email" className="px-8 py-3 bg-white text-blue-900 font-semibold rounded-xl hover:bg-blue-50 transition-colors shadow-lg inline-block">
              Start Contributing
            </Link>
          ) : (
            <div className="flex gap-3 justify-center">
              <Link to="/signup" className="px-8 py-3 bg-white text-blue-900 font-semibold rounded-xl hover:bg-blue-50 transition-colors shadow-lg inline-block">
                Create Your Account
              </Link>
              <Link to="/login" className="px-8 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors border border-white/30 inline-block">
                Sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

